<template>
  <div class="calibrations-page">
    <div class="page-header flex space-between align-center mb-3">
      <div>
        <h1 class="text-display ml-4">{{ modelName || 'Модель' }}</h1>
        <p class="text-muted ml-4" v-if="mixerName">{{ mixerName }}</p>
      </div>
      <div>
        <v-btn class="ml-4" medium @click="showAddCalibration = true" :disabled="isLoadingCalibrations">
          <v-icon position-left font-size="1" icon="plus"></v-icon>
          <span>Додати інформацію</span>
        </v-btn>
      </div>
    </div>

    <div v-if="isLoadingCalibrations" class="loading-container empty-state">
      <span>Завантаження калібрувань...</span>
    </div>

    <div v-else-if="calibrationsError" class="error-state">
      <p>Помилка завантаження: {{ calibrationsError.message }}</p>
      <v-btn @click="loadData">Спробувати знову</v-btn>
    </div>

    <div v-else-if="calibrationsCount === 0" class="empty-state">
      <p>Немає калібрувань для цієї моделі</p>
    </div>

    <div v-else class="calibrations-list">
      <div class="calibrations-header">
        <div class="header-name">Інформація про міксер</div>
        <div class="header-value">Калібрування</div>
        <div class="header-actions"></div>
      </div>
      
      <div v-for="calibration in allCalibrations" :key="calibration.id" class="calibration-item">
        <div class="calibration-content">
          <div class="calibration-name">{{ calibration.name }}</div>
          <div class="calibration-value">{{ calibration.calibration }}</div>
        </div>
        <div class="calibration-actions">
          <v-btn class="v-btn--icon v-btn--default" small @click="editCalibration(calibration)">
            <v-icon font-size="1" icon="cog"></v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Модалки -->
    <v-dialog max-width="400" v-model="showAddCalibration">
      <AddCalibrationModal @close="showAddCalibration = false" @create="handleAddCalibration" />
    </v-dialog>

    <v-dialog max-width="400" v-model="showEditCalibration">
      <EditCalibrationModal :selected="selectedCalibration" @close="showEditCalibration = false" 
        @update="handleCalibrationUpdated" @delete="handleCalibrationDeleted" />
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import AddCalibrationModal from '../modals/AddCalibrationModal.vue'
import EditCalibrationModal from '../modals/EditCalibrationModal.vue'

export default {
  name: 'CalibrationsView',
  components: { AddCalibrationModal, EditCalibrationModal },

  props: {
    mixerId: {
      type: String,
      required: true
    },
    modelId: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      showAddCalibration: false,
      showEditCalibration: false,
      selectedCalibration: null
    }
  },

  computed: {
    ...mapState('baseMixers', ['currentMixer', 'currentModel']),
    ...mapGetters('baseMixers', [
      'allCalibrations',
      'mixerName',
      'modelName',
      'calibrationsCount',
      'isLoadingCalibrations',
      'calibrationsError'
    ])
  },

  async mounted () {
    await this.loadData()
  },

  watch: {
    modelId: {
      handler: async function (newModelId) {
        if (newModelId) {
          await this.loadData()
        }
      },
      immediate: false
    }
  },

  methods: {
    ...mapActions('baseMixers', [
      'loadMixer',
      'loadModel',
      'loadCalibrations',
      'createCalibration',
      'updateCalibration',
      'deleteCalibration',
      'clearCalibrations'
    ]),

    async loadData () {
      try {
        this.clearCalibrations()

        await Promise.all([
          this.loadMixer(this.mixerId),
          this.loadModel(this.modelId),
          this.loadCalibrations(this.modelId)
        ])
      } catch (error) {
        console.error('Error loading data:', error)
        this.$toasted.error('Не вдалося завантажити дані')
      }
    },

    editCalibration (calibration) {
      this.selectedCalibration = { ...calibration }
      this.showEditCalibration = true
    },

    async handleAddCalibration (calibrationData) {
      try {
        const payload = {
          ...calibrationData,
          model: +this.modelId
        }

        await this.createCalibration(payload)
        this.showAddCalibration = false
        this.$toasted.success('Калібрування успішно додано')
      } catch (error) {
        console.error('Error creating calibration:', error)
        this.$toasted.error('Не вдалося створити калібрування')
      }
    },

    async handleCalibrationUpdated (updatedCalibrationData) {
      if (!updatedCalibrationData) {
        console.error('No calibration data received from modal')
        this.$toasted.error('Немає даних для оновлення калібрування')
        return
      }

      try {
        await this.updateCalibration({
          id: updatedCalibrationData.id,
          data: updatedCalibrationData
        })
        this.showEditCalibration = false
        this.$toasted.success('Калібрування успішно оновлено')
      } catch (error) {
        console.error('Error updating calibration:', error)
        this.$toasted.error('Не вдалося оновити калібрування')
      }
    },

    async handleCalibrationDeleted (calibrationId) {
      if (!calibrationId) {
        console.error('No calibration ID received from modal')
        this.$toasted.error('Немає ID для видалення калібрування')
        return
      }

      try {
        await this.deleteCalibration(calibrationId)
        this.showEditCalibration = false
        this.$toasted.success('Калібрування успішно видалено')
      } catch (error) {
        console.error('Error deleting calibration:', error)
        this.$toasted.error('Не вдалося видалити калібрування')
      }
    }
  },

  beforeDestroy () {
    this.clearCalibrations()
  }
}
</script>

<style scoped>
.calibrations-page {
  padding: 0;
}

.calibrations-list {
  overflow: hidden;
  margin-top: 3.4rem;
}

.calibrations-header {
  border: 1px solid #e1e5e9;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 3.5rem 4rem;
  background-color: #fff;
  font-weight: 400;
  color: #6c757d;
  font-size: 1.6rem;
  margin-bottom: 3.4rem;
}

.header-name {
  flex-grow: 1;
}

.header-value {
  margin-right: 1rem;
  text-align: right;
}

.header-actions {
  width: 32px;
}

.calibration-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 2rem 4rem;
  border-bottom: 1px solid #f1f3f4;
  background: white;
  transition: background-color 0.2s ease;
  margin-bottom: 2rem;
  border-radius: 1rem;
}

.calibration-item:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.calibration-item:hover {
  background-color: #f8f9fa;
}

.calibration-content {
  display: flex;
  align-items: center;
  flex-grow: 1;
  justify-content: space-between;
  margin-right: 1rem;
  
}

.calibration-name {
    font-size: 1.6rem;
  font-weight: 400;
  color: #212529;
  flex-shrink: 0;
}

.calibration-value {
   font-size: 1.6rem;
  font-weight: 400;
  text-align: right;
}

.calibration-actions {
  flex-shrink: 0;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
}

.empty-state {
  margin-top: 3.4rem;
  text-align: center;
  padding: 2rem;
  color: #666;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.error-state {
  text-align: center;
  padding: 2rem;
  color: #d32f2f;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.text-muted {
  color: #666;
  font-size: 1.4rem;
}
</style>